// NOTE: USING INLINE STYLES BECAUSE TAILWIND CSS DOES NOT SUPPORT DYNAMIC VALUES FROM PROPS. THE FOLLOWING LINK EXPLAINS WHY:
//https://v2.tailwindcss.com/docs/just-in-time-mode#arbitrary-value-support
import React from 'react';
import Webcam from 'react-webcam';
import { TeleprompterText } from './TeleprompterText';
import { SpeedController } from './SpeedController';
import { FontSizeController } from './FontSizeController';
import { OpacityController } from './OpacityController';
import { uploadFile } from '../../data/uploadFile';
import { FileUpload } from '../../types/Files/FileUpload';
import { Button, ButtonGreen, ButtonRed } from '../buttons/';
import { WebCamComponentProps } from '../../types/Camera/WebCamComponentProps';
import { useOrientation } from '@uidotdev/usehooks';
import * as Sentry from '@sentry/browser';

export const WebCamVideo = (props: WebCamComponentProps) => {
  const orientation = useOrientation();
  const portraitWebcamRef = React.useRef<Webcam>(null);
  const portraitMediaRecorderRef = React.useRef<MediaRecorder>();
  const landscapeWebcamRef = React.useRef<Webcam>(null);
  const landscapeMediaRecorderRef = React.useRef<MediaRecorder>();
  const [capturing, setCapturing] = React.useState(false);
  const [reviewing, setReviewing] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const [speed, setSpeed] = React.useState(75);
  const [textSize, setTextSize] = React.useState(20);
  const [opacity, setOpacity] = React.useState('0.65');
  const [reviewVideoSrc, setReviewVideoSrc] = React.useState<string | undefined>();
  const [recordedChunks, setRecordedChunks] = React.useState<Array<Blob>>([]);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [previewNeeded, setPreviewNeeded] = React.useState<boolean>(true);
  const [ready, setReady] = React.useState<boolean>(false);

  React.useEffect(() => {
    // HIDE THE WEBCAM UI UNTIL THE USER HAS CLICKED ALLOW.
    // THIS PREVENTS THE USER FROM FORCING AN INITIAL CAMERA TO LOAD THAT DOESN'T MAINTAIN THE CORRECT REF.
    // IF THEY RECORD WITH THIS CAMERA, THEY DO NOT SEE THE GREEN REVIEW & UPLOAD BUTTON
    // THIS CAN STILL HAPPEN IF THEY CLICK ALLOW AFTER THIS TIMEOUT HAS EXPIRED
    // THEY HAVE TO START THE SCROLL PREVIEW BEFORE THE NEW CAMERA REF IS CREATED
    setTimeout(() => {
      setReady(true);
    }, 4000);
  });

  React.useEffect(() => {
    setPreviewNeeded(true);
  }, [speed, textSize, opacity]);

  const portraitVideoConstraints = {
    height: window.innerHeight * 0.75,
    width: window.innerHeight * 0.75 * (640 / 480),
    aspectRatio: 480 / 640,
    facingMode: 'user',
  };

  const landscapeVideoConstraints = {
    height: window.innerHeight * 0.75,
    width: window.innerHeight * 0.75 * (480 / 640),
    aspectRatio: 640 / 480,
    facingMode: 'user',
  };

  // RELOAD THE COMPONENT ON ORIENTATION CHANGE
  React.useEffect(() => {
    navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });
    if (orientation.type.includes('portrait')) {
      portraitWebcamRef.current?.render();
      portraitWebcamRef.current?.forceUpdate();
    }
    if (orientation.type.includes('landscape')) {
      landscapeWebcamRef.current?.render();
      landscapeWebcamRef.current?.forceUpdate();
    }
  }, [orientation]);

  const handleStartCaptureClick = () => {
    setCapturing(true);
    setIsScrolling(true);
    const handleDataAvailable = (blob: BlobEvent) => {
      const { data } = blob;
      if (data.size > 0) {
        setRecordedChunks((prev: Array<Blob>) => prev.concat(data));
        setReviewVideoSrc(URL.createObjectURL(data));
      }
    };
    // MAINTAINING TWO REFS ALLOWS USER TO SWAP CAMERAS WITHOUT REFRESHING PAGE
    if (orientation.type.includes('portrait') && portraitWebcamRef.current?.stream) {
      portraitMediaRecorderRef.current = new MediaRecorder(portraitWebcamRef?.current?.stream, {
        // mimeType: "video/webm",  IGNORING FOR CROSS BROWSER SUPPORT
      });
      portraitMediaRecorderRef.current.addEventListener('dataavailable', (event) => handleDataAvailable(event));
      portraitMediaRecorderRef.current.start();
    }
    if (orientation.type.includes('landscape') && landscapeWebcamRef.current?.stream) {
      landscapeMediaRecorderRef.current = new MediaRecorder(landscapeWebcamRef?.current?.stream, {
        // mimeType: "video/webm",  IGNORING FOR CROSS BROWSER SUPPORT
      });
      landscapeMediaRecorderRef.current.addEventListener('dataavailable', (event) => handleDataAvailable(event));
      landscapeMediaRecorderRef.current.start();
    }
  };

  // const handleStartCaptureClick = React.useCallback(() => {
  //   setCapturing(true);
  //   setIsScrolling(true);
  //   const handleDataAvailable = (blob: BlobEvent) => {
  //     const { data } = blob;
  //     if (data.size > 0) {
  //       setRecordedChunks((prev: Array<Blob>) => prev.concat(data));
  //       setReviewVideoSrc(URL.createObjectURL(data));
  //     }
  //   };
  //   // MAINTAINING TWO REFS ALLOWS USER TO SWAP CAMERAS WITHOUT REFRESHING PAGE
  //   if (orientation.type.includes('portrait') && portraitWebcamRef.current?.stream) {
  //     portraitMediaRecorderRef.current = new MediaRecorder(portraitWebcamRef?.current?.stream, {
  //       // mimeType: "video/webm",  IGNORING FOR CROSS BROWSER SUPPORT
  //     });
  //     portraitMediaRecorderRef.current.addEventListener('dataavailable', (event) => handleDataAvailable(event));
  //     portraitMediaRecorderRef.current.start();
  //   }
  //   if (orientation.type.includes('landscape') && landscapeWebcamRef.current?.stream) {
  //     landscapeMediaRecorderRef.current = new MediaRecorder(landscapeWebcamRef?.current?.stream, {
  //       // mimeType: "video/webm",  IGNORING FOR CROSS BROWSER SUPPORT
  //     });
  //     landscapeMediaRecorderRef.current.addEventListener('dataavailable', (event) => handleDataAvailable(event));
  //     landscapeMediaRecorderRef.current.start();
  //   }
  // }, [
  //   portraitWebcamRef,
  //   landscapeWebcamRef,
  //   orientation,
  //   setCapturing,
  //   portraitMediaRecorderRef,
  //   landscapeMediaRecorderRef,
  // ]);

  const handleStopCaptureClick = React.useCallback(() => {
    portraitMediaRecorderRef?.current?.stop();
    landscapeMediaRecorderRef?.current?.stop();
    setCapturing(false);
    setIsScrolling(false);
  }, [portraitMediaRecorderRef, landscapeMediaRecorderRef]);

  const handleReRecord = React.useCallback(() => {
    setIsScrolling(false);
    setCapturing(false);
    setReviewing(false);
    setRecordedChunks([]);
  }, []);

  const handleUserMediaError = async (error: string | DOMException) => {
    console.error('Error opening webcam:', error);

    if (typeof error === 'string') {
      if (error.includes('NotAllowedError')) {
        Sentry.captureException(error);
        alert(
          'Camera access is required to record video. Please refresh the page and allow camera and microphone access to continue. If you are unable to grant permissions on this device, please try on a different device.',
        );
      } else if (error.includes('NotFoundError')) {
        Sentry.captureException(error);
        alert('No camera available.  Please try again on a device with a usable camera.');
      } else if (error.includes('NotReadableError')) {
        Sentry.captureException(error);
        alert('Camera is already in use. Please refresh the page and try again.');
      } else {
        Sentry.captureException(error);
        alert('An error occurred while opening webcam. Please contact support if you continue to have this issue.');
      }
    } else {
      if (error.name === 'NotAllowedError') {
        Sentry.captureException(error);
        alert(
          'Camera access is required to record video. Please refresh the page and allow camera and microphone access to continue. If you are unable to grant permissions on this device, please try on a different device.',
        );
      } else if (error.name === 'NotFoundError') {
        Sentry.captureException(error);
        alert('No camera available. Please try again on a device with a usable camera.');
      } else if (error.name === 'NotReadableError') {
        Sentry.captureException(error);
        alert('Camera is already in use. Please refresh the page and try again.');
      } else {
        Sentry.captureException(error);
        alert('An error occurred while opening webcam. Please contact support if you continue to have this issue.');
      }
    }
  };

  const handleUpload = React.useCallback(() => {
    setIsUploading(true);
    if (recordedChunks.length) {
      let type = recordedChunks[0].type;
      // TODO: ENABLE THE LOG BELOW & CHECK HERE FIRST FOR NEW CODEC IF TRANSCODER FAILS ON CERTAIN BROWSERS
      // console.warn('Patient Portal uploading Video of type:', type);
      // HANDLE PIXEL CHROME BROWSER ISSUE
      if (type === 'video/x-matroska;codecs=avc1,opus') {
        type = 'video/webm';
      }
      const blob = new Blob(recordedChunks, {
        type: recordedChunks[0].type,
      });
      const payload = {
        bucket: 'videos',
        path: `${props.patientId}/originalVideo`,
        file: blob,
      } as FileUpload;

      const uploadTask = uploadFile(payload);
      if (!uploadTask) {
        Sentry.captureMessage('Unable to create upload task');
        console.error('Unable to create upload task');
        setIsUploading(false);
        alert(
          'Unable to connect to upload server. This is likely due to your current network quality or configuration. Please try again from your personal network without a VPN. If this problem persists, please notify support.',
        );
        return;
      }
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.info('Upload is ' + progress + '% done');
        },
        (error) => {
          Sentry.captureException(error);
          console.error(error);
          setIsUploading(false);
          alert(`Error uploading file: ${error.message}`);
        },
        () => {
          setIsUploading(false);
          if (props.setNextEnabled) {
            props.setNextEnabled(true);
          }
          alert('Upload successful. You may now click Next at the bottom of this page.');
        },
      );

      setRecordedChunks([]);
    }
  }, [recordedChunks, props.patientId]);

  if (isUploading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <div role="status">
          <svg
            aria-hidden="true"
            className="h-64 w-64 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
        <div>Please wait while we upload your video</div>
      </div>
    );
  }

  return (
    <>
      {!ready ? (
        <div>Loading...</div>
      ) : (
        <>
          <div
            id="webcam-video-component"
            style={{
              maxWidth: window.innerWidth * 0.8,
            }}
          >
            {!reviewing && (
              <div className="">
                <div className="w-full flex-row">
                  <div className="relative overflow-hidden">
                    {/* GENERATED DIV IS TWICE AS BIG ON SAFARI */}
                    <div
                      id="transparency-overlay"
                      style={{
                        position: 'absolute',
                        top: 0,
                        backgroundColor: 'white',
                        opacity: opacity,
                        height: '100%',
                        width: '100%',
                      }}
                    />
                    {orientation.type.includes('portrait') ? (
                      <div className="flex flex-row">
                        <Webcam
                          id="webcamView"
                          audio={true}
                          muted={true}
                          ref={portraitWebcamRef}
                          videoConstraints={portraitVideoConstraints}
                          onUserMediaError={(err) => handleUserMediaError(err)}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-row">
                        <Webcam
                          id="webcamView"
                          audio={true}
                          muted={true}
                          ref={landscapeWebcamRef}
                          videoConstraints={landscapeVideoConstraints}
                          onUserMediaError={handleUserMediaError}
                        />
                      </div>
                    )}
                    <div className="absolute top-5 h-full w-full">
                      <TeleprompterText
                        isScrolling={isScrolling}
                        setIsScrolling={setIsScrolling}
                        speed={speed}
                        textSize={textSize}
                      />
                    </div>
                  </div>
                  <FontSizeController textSize={textSize} setTextSize={setTextSize} scrolling={isScrolling} />
                  <SpeedController speed={speed} setSpeed={setSpeed} scrolling={isScrolling} />
                  <OpacityController opacity={opacity} setOpacity={setOpacity} scrolling={isScrolling} />
                </div>
              </div>
            )}
            <div className="flex flex-row">
              {reviewing && reviewVideoSrc && (
                <div>
                  {/* eslint-disable-next-line */}
                  <video src={reviewVideoSrc} controls />
                </div>
              )}
            </div>
            {capturing ? (
              <div className="flex flex-row justify-center">
                <Button
                  type="button"
                  id="stop-recording-button"
                  onClick={handleStopCaptureClick}
                  textContent="Stop Recording"
                />
              </div>
            ) : recordedChunks.length === 0 && !reviewing ? (
              previewNeeded ? (
                <div className="flex flex-row items-center justify-center">
                  <Button
                    type="button"
                    id="preview-button"
                    onClick={() => {
                      setIsScrolling(true);
                      setPreviewNeeded(false);
                    }}
                    textContent="Preview Scroll"
                  />
                </div>
              ) : isScrolling ? (
                <div className="flex flex-row justify-evenly">
                  <Button
                    type="button"
                    id="preview-button"
                    onClick={() => {
                      setIsScrolling(false);
                      setPreviewNeeded(false);
                    }}
                    textContent="Stop Preview"
                  />
                  <Button
                    type="button"
                    id="start-recording-button"
                    onClick={handleStartCaptureClick}
                    textContent="Record"
                  />
                </div>
              ) : (
                <div className="flex flex-row justify-evenly">
                  <Button
                    type="button"
                    id="preview-button"
                    onClick={() => {
                      setIsScrolling(true);
                      setPreviewNeeded(false);
                    }}
                    textContent="Preview Scroll"
                  />
                  <Button
                    type="button"
                    id="start-recording-button"
                    onClick={handleStartCaptureClick}
                    textContent="Record"
                  />
                </div>
              )
            ) : null}
            {recordedChunks.length > 0 && reviewing ? (
              <div className="flex flex-row justify-evenly">
                <Button type="button" id="upload-button" onClick={handleUpload} textContent="Upload" />
                <ButtonRed type="button" id="re-record-button" onClick={handleReRecord} textContent="Re-Record" />
              </div>
            ) : recordedChunks.length > 0 ? (
              <div className="flex flex-col items-center">
                <p className="text-xl font-bold">Click the green button to review your video and submit.</p>
                <div className="flex flex-row justify-evenly">
                  <ButtonGreen
                    type="button"
                    id="review-button"
                    onClick={() => setReviewing(true)}
                    textContent="Review & Upload"
                  />
                  <ButtonRed type="button" id="re-record-button" onClick={handleReRecord} textContent="Re-Record" />
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
    </>
  );
};
